import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../utils/permissions';
import { errorText } from '../../utils/errorText';
import { box } from '../shared/styles';
import ProtocolEditor from './ProtocolEditor';

//: Recently used protocols, kept on this device per person (a per-viewer convenience).
const RECENT_LIMIT = 5;
const recentKey = (userId) => `mb_protocols_recent:${userId || 'anon'}`;
export function readRecentProtocols(userId) {
  try {
    const list = JSON.parse(localStorage.getItem(recentKey(userId)) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function rememberProtocol(userId, id) {
  const next = [{ id, at: new Date().toISOString() }, ...readRecentProtocols(userId).filter((r) => r.id !== id)]
    .slice(0, RECENT_LIMIT);
  try { localStorage.setItem(recentKey(userId), JSON.stringify(next)); } catch { /* storage off: this visit only */ }
  return next;
}
//: Case- and accent-insensitive, so "pda" finds "PDA" and Hebrew niqqud does not get in the way.
const fold = (v) => String(v || '').normalize('NFKD').replace(/[\u0300-\u036f\u0591-\u05c7]/g, '').toLowerCase();

/*
 * Protocols (Task 6.4): the shared protocols and your own variants, written by
 * hand (equipment by type and model) -- always editable, every save a signed
 * version with its reason.
 */
export default function Protocols() {
  const { t } = useTranslation('protocols');
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [mode, setMode] = useState('view'); // view | new
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState(() => readRecentProtocols(user?.user_id));
  const canAuthor = can(user, 'protocol:author');

  const load = useCallback(() => api.get('/protocols').then(({ data }) => setList(data))
    .catch((e) => setError(errorText(e))), []);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    setError('');
    setMode('view');
    try {
      const { data } = await api.get(`/protocols/${id}`);
      setDetail(data);
      setRecent(rememberProtocol(user?.user_id, id));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const saveNew = async (body) => {
    const { data } = await api.post('/protocols', body);
    await load();
    await open(data.protocol_id);
  };
  const saveVersion = async (body) => {
    await api.post(`/protocols/${detail.protocol_id}/versions`, body);
    await open(detail.protocol_id);
  };
  const makeVariant = async (body) => {
    const { data } = await api.post(`/protocols/${detail.protocol_id}/variants`, body);
    await load();
    await open(data.protocol_id);
  };

  const shown = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return list;
    return list.filter((p) => [p.name, p.description, p.category, p.kind === 'variant' ? t('myVariant') : t('shared')]
      .some((v) => fold(v).includes(q)));
  }, [list, query, t]);
  const recentProtocols = useMemo(() => recent.map((r) => list.find((p) => p.protocol_id === r.id)).filter(Boolean),
    [recent, list]);

  const isVariant = detail?.kind === 'variant';
  const ownVariant = isVariant && detail.owner_id === user?.user_id;
  const canEdit = isVariant ? ownVariant : canAuthor;

  return (
    <div>
      <h2>{t('pageTitle')}</h2>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0 }}>{t('intro')}</p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('searchPlaceholder')}
        aria-label={t('searchPlaceholder')} title={t('tip.search')} data-testid="protocol-search"
        style={{ width: '100%', maxWidth: 520, padding: '10px 12px', fontSize: 16, borderRadius: 8,
          border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-fg)', boxSizing: 'border-box',
          marginBottom: 12 }} />
      {recentProtocols.length > 0 && !query.trim() && (
        <section aria-labelledby="protocols-recent-title" style={{ marginBottom: 12 }} data-testid="protocols-recent">
          <h3 id="protocols-recent-title" style={{ fontSize: 15, margin: '0 0 6px' }}>{t('recentTitle')}</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {recentProtocols.map((p) => (
              <button key={p.protocol_id} type="button" onClick={() => open(p.protocol_id)} title={t('tip.openProtocol')}
                data-testid={`recent-protocol-${p.protocol_id}`}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--c-border)', cursor: 'pointer',
                  background: detail?.protocol_id === p.protocol_id ? 'var(--c-bg-subtle)' : 'var(--c-surface)',
                  color: 'var(--c-text-fg)', textAlign: 'start' }}>
                <strong>{p.name}</strong>{' '}
                <small style={{ color: 'var(--c-text-muted-fg)' }}>v{p.current_version}</small>
              </button>
            ))}
          </div>
        </section>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {canAuthor && (
          <button type="button" data-testid="new-protocol" title={t('tip.newProtocol')} onClick={() => { setDetail(null); setMode('new'); }}>
            {t('newProtocol')}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <ul style={{ ...box, listStyle: 'none', minWidth: 240, flex: '0 1 300px', margin: 0 }}>
          {list.length === 0 && <li>{t('empty')}</li>}
          {list.length > 0 && shown.length === 0 && <li data-testid="protocol-no-match">{t('noMatch', { query })}</li>}
          {shown.map((p) => (
            <li key={p.protocol_id}>
              <button type="button" data-testid={`protocol-${p.protocol_id}`} title={t('tip.openProtocol')} onClick={() => open(p.protocol_id)}
                style={{ background: 'none', border: 'none', padding: '6px 0', cursor: 'pointer', textAlign: 'start',
                  color: 'var(--c-text-fg)', width: '100%' }}>
                <strong>{p.name}</strong>
                <br />
                <small style={{ color: 'var(--c-text-muted-fg)' }}>
                  {p.kind === 'variant' ? t('myVariant') : t('shared')} · v{p.current_version}
                </small>
              </button>
            </li>
          ))}
        </ul>
        <div style={{ flex: '1 1 520px' }}>
          {mode === 'new' && (
            <ProtocolEditor key="new" isNew canEdit={canAuthor} canAuthor={false} onSave={saveNew} />
          )}
          {mode === 'view' && detail && (
            <ProtocolEditor key={`${detail.protocol_id}:${detail.current_version}`} detail={detail}
              canEdit={canEdit} notYours={isVariant && !ownVariant} canAuthor={canAuthor}
              onSave={saveVersion} onVariant={makeVariant} />
          )}
          {mode === 'view' && !detail && <p>{t('choose')}</p>}
        </div>
      </div>
    </div>
  );
}
